import { useEffect, useState } from 'react';
import { AudioTrack, AudioSelectType, settingControllerKey } from '../../../Player/PlayerModels';
import {
  CustomOption,
  CustomOptionWrapper,
  SettingPanelMultiSelectItem,
  SelectingItemMainWrapper,
} from '../../../Player/styles';
import { Box } from '../../../../styles';
import IconAudio from '../../../../icons/IconAudio';
import { PanelSettingHeader, PanelSettingLayer } from '../../../Player/styles/Panel';
import { findCaptionLabel } from '../../../Player/PlayerServices';
import UsePlayerProps from '../../../../Store/usePlayerProps';

export default function ControlBarAudioPanel({ handleSettingItemClick, activeSetting }: {
  handleSettingItemClick: (controllerKey: settingControllerKey) => void
  activeSetting: string | null
}) {
  const video: any = document.getElementById('simple-player');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAudioTracks, setSelectedAudioTracks] = useState<{ language: string, label: string }>();
  const [audioTracks, setAudioTracks] = useState<AudioTrack[]>();

  const persistAudioLang = (language: string) => {
    const current = UsePlayerProps.getState().playerState;
    if (current.audioState?.selectedLang === language) return;
    UsePlayerProps.getState().setPlayerState({ ...current, audioState: { selectedLang: language } });
  };

  // Activates the audio track: prefer the user's previously chosen language (persisted in
  // the store so it survives the panel remounting after an ad), then the Persian track,
  // then the first track. Without this, every remount (e.g. resuming from a midroll) reset
  // the audio back to the default.
  const applyAudioSelection = () => {
    const tracks: any = video?.audioTracks;
    if (!tracks || tracks.length === 0) return false;
    const all: any[] = Array.from(tracks);
    const storedLang = UsePlayerProps.getState().playerState.audioState?.selectedLang;
    let selected = storedLang ? all.find((t) => t.language === storedLang) : undefined;
    if (!selected) selected = all.find((t) => t.language === 'fa' || t.language === 'per');
    if (!selected) [selected] = all;
    if (!selected) return false;
    all.forEach((t) => { t.enabled = t.language === selected.language; });
    setSelectedAudioTracks({ language: selected.language, label: findCaptionLabel(selected.language) });
    setAudioTracks(all.map((t) => ({
      id: t.id,
      kind: t.kind,
      label: findCaptionLabel(t.language),
      language: t.language,
      enabled: t.language === selected.language,
    })));
    persistAudioLang(selected.language);
    return true;
  };

  useEffect(() => {
    if (!video?.audioTracks) return;
    // Tracks may not be parsed yet on (re)mount; retry once shortly after.
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!applyAudioSelection()) timer = setTimeout(applyAudioSelection, 1000);

    const syncFromNative = () => {
      const enabled = (Array.from(video.audioTracks) as any[]).find((t) => t.enabled);
      if (enabled) {
        setSelectedAudioTracks({ language: enabled.language, label: findCaptionLabel(enabled.language) });
      }
    };
    video.audioTracks.addEventListener('change', syncFromNative);
    return () => {
      if (timer) clearTimeout(timer);
      video.audioTracks.removeEventListener('change', syncFromNative);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMenu = (e, key) => {
    e.stopPropagation();
    handleSettingItemClick(key);
    setIsOpen(!isOpen);
  };

  const handleAudioTracksChange = (language: string) => {
    const tracks: any = video?.audioTracks;
    if (!tracks) return;
    Array.from(tracks).forEach((t: any) => { t.enabled = t.language === language; });
    setSelectedAudioTracks({ language, label: findCaptionLabel(language) });
    persistAudioLang(language);
  };

  return (
    <>
      {!activeSetting &&
        <SettingPanelMultiSelectItem icon={<IconAudio />} label="player.audio" onClick={(e) => toggleMenu(e, 'audio')}
          value={selectedAudioTracks?.label as string} />}
      {activeSetting === 'audio' && <PanelSettingLayer>
        {audioTracks && selectedAudioTracks &&
          <AudioSelect options={audioTracks} returnAction={(e) => toggleMenu(e, null)} onChange={(language) => {
            if (language !== selectedAudioTracks?.language) {
              handleAudioTracksChange(language);
              setIsOpen(false);
            }
          }} value={selectedAudioTracks.language} />}
      </PanelSettingLayer>}
    </>
  );
}
const AudioSelect = ({ value: selectValue, options, onChange, returnAction }: AudioSelectType) => {
  return (
    <Box>
      <PanelSettingHeader label={'player.audio'} handleClick={(e) => returnAction(e)} />
      <SelectingItemMainWrapper>
        {options.map((item: AudioTrack) => (
          <CustomOptionWrapper key={item.id} onClick={(e: any) => {
            onChange(item.language, item.label);
            returnAction(e);
          }}>
            <CustomOption selected={item.language === selectValue}>
              {item.label}
            </CustomOption>
          </CustomOptionWrapper>
        ))}
      </SelectingItemMainWrapper>
    </Box>
  );
};
